// Wood recipe — SEAM-FREE continuous wood (no planks).
// `woodSurface` composes: warped-axis growth rings + anisotropic grain + rare knots in WORLD
// space — there is NO board lattice and NO inter-board gap, so the material reads as a single
// continuous piece of wood (no dark plank lines/seams).  The former planks/board geometry was
// forked out (2026-08-24) into its own `Recipe_WoodPlanks` (`woodSurface`'s shared GLSL keeps
// the rings -> warped-axis-distance composition from the old `domainRepeat` body).
//
// Unlike the old five-stage single-layer body (which had to consume BOARD-LOCAL coords inside one
// monolithic `domainRepeat` layer), every stage here consumes the SAME world-space `p`, so the
// composition stays clean.  `woodSurface` is a prelude-layer-body primitive (composes the existing
// wavesVal/radialDistToWarpedAxis/fbmVal/hash01/rampVal helpers; see the PrimitiveRegistry note).
//
// SCALE CONTRACT:
//   continuous surface (no boards) — the whole texture is a warped field
//   rings   ~1.2 cm spacing — the dominant colour feature
//   grain   ~2 cm cells stretched 20:1 along the axis — the fine streaking
//   knots   rare, ~2 cm, one candidate per ~0.6 m world-space tile
//
// WEAKNESS (plan §4.3): wood is "excellent" for plank/decking faces but weak for END GRAIN and
// joinery — this recipe only models the face, which is the only thing a top-down view shows.
import {MaterialLayer, MaterialRecipe, ParamSchema} from "../@MaterialTypes.js";

export const WOOD_RECIPE_ID = "wood";

export const woodRecipe = new MaterialRecipe({
	id: WOOD_RECIPE_ID,
	name: "Wood",
	roughness: 0.45,
	params: [
		new ParamSchema({key: "ring_wavelength", label: "Ring spacing (m)", kind: "scalar", min: 0.002, max: 0.2, default: 0.012}),
		// > 1 narrows the dark ring lines, which is what distinguishes wood's crisp growth rings
		// from marble's soft veining (same primitive underneath).
		new ParamSchema({key: "ring_sharpness", label: "Ring sharpness", kind: "scalar", min: 0.05, max: 8, default: 1.8}),
		new ParamSchema({key: "grain_scale", label: "Grain scale (m)", kind: "scalar", min: 0.002, max: 0.5, default: 0.02}),
		// The anisotropy IS the wood look: features are stretched ~20:1 along the axis.
		new ParamSchema({key: "grain_aspect", label: "Grain anisotropy", kind: "scalar", min: 1, max: 50, default: 20}),
		// The ring-axis warp: this is what turns concentric-looking bands into the characteristic
		// "cathedral" figure where the sawn face crosses the growth cone at a shallow angle.
		new ParamSchema({key: "ring_warpScale", label: "Ring warp scale (m)", kind: "scalar", min: 0.02, max: 10, default: 0.6}),
		new ParamSchema({key: "ring_warpAmount", label: "Ring warp amount (m)", kind: "scalar", min: 0, max: 1, default: 0.06}),
		// t = 0 is the ring core / knot (darkest), t = 1 the pale latewood between rings.
		new ParamSchema({key: "wood_ramp", label: "Wood ramp", kind: "ramp", default: [0, 0.22, 0.12, 0.06, 0.35, 0.42, 0.25, 0.12, 0.7, 0.6, 0.4, 0.22, 1, 0.72, 0.53, 0.32]}),
		// "Rare knots" per the plan: a per-tile probability, so a knot is a whole-tile property
		// (at most one per ~0.6 m tile) rather than a scattered field.
		new ParamSchema({key: "knot_chance", label: "Knot chance per tile", kind: "scalar", min: 0, max: 1, default: 0.06}),
		new ParamSchema({key: "knot_radius", label: "Knot radius (m)", kind: "scalar", min: 0, max: 0.1,
			// commented; disable knots for now (current impl. is too ugly)
			//default: 0.02,
			default: 0,
		}),
		// The world-space tile a knot is localised to (a knot is NOT a per-board property when
		// there are no boards).  Structural knob — how sparse the (currently disabled) knots are.
		new ParamSchema({key: "ring_spacing", label: "Knot tile (m)", kind: "scalar", min: 0.2, max: 5, default: 0.6}),
		new ParamSchema({key: "polish_scale", label: "Surface scale (m)", kind: "scalar", min: 0.002, max: 0.5, default: 0.03}),
		new ParamSchema({key: "polish_height", label: "Surface relief (m)", kind: "scalar", min: 0, max: 0.01, default: 0.0002}),
	],
	layers: [
		// The continuous wood surface: rings + grain + knots — no seams/plank-lines.
		new MaterialLayer({
			id: "surface",
			primitive: "woodSurface",
			args: {
				grainScale: {ref: "grain_scale"},
				grainAspect: {ref: "grain_aspect"},
				ringWavelength: {ref: "ring_wavelength"},
				ringSharpness: {ref: "ring_sharpness"},
				ringSpacing: {ref: "ring_spacing"},
				warpScale: {ref: "ring_warpScale"},
				warpAmount: {ref: "ring_warpAmount"},
				woodRamp: {ref: "wood_ramp"},
				knotChance: {ref: "knot_chance"},
				knotRadius: {ref: "knot_radius"},
				// A hair of ring relief so the surface isn't perfectly flat under the specular
				// lobe (no plank crown here — that was the planks version).
				heightScale: {ref: "polish_height"},
			},
			blend: "over",
			contributes: "both",
		}),
		// A hair of surface relief so a planed board is not perfectly flat under the specular lobe.
		new MaterialLayer({
			id: "polish",
			primitive: "fbm",
			args: {
				scale: {ref: "polish_scale"},
				octaves: 3,
				lacunarity: 2,
				persistence: 0.5,
				mode: 0,
				heightScale: {ref: "polish_height"},
			},
			blend: "over",
			contributes: "height",
		}),
	],
});
